import React, { useState, useEffect, useCallback } from 'react';
import { UserMinus, Plus, Eye } from 'lucide-react';
import * as dpService from 'features/dp/services/dpService';

const inputClass =
  'w-full px-3 py-2 text-sm rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text)] focus:outline-none focus:ring-2 focus:ring-teal-500';

function formatarData(iso) {
  if (!iso) return '—';
  const [a, m, d] = String(iso).substring(0, 10).split('-');
  return a && m && d ? `${d}/${m}/${a}` : iso;
}

function formatarMoeda(v) {
  return Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export default function RescisoesTab({ podeEditar }) {
  const [rescisoes, setRescisoes] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [detalhe, setDetalhe] = useState(null);
  const [funcionarios, setFuncionarios] = useState([]);

  const [form, setForm] = useState({
    funcionario_id: '', tipo_rescisao: 'sem_justa_causa',
    data_aviso: '', data_desligamento: '', motivo: '',
  });

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const res = await dpService.listarRescisoes({ per_page: 50 });
      setRescisoes(res.data?.data?.data ?? res.data?.data ?? []);
    } catch {
      setErro('Erro ao carregar rescisões');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    dpService.listarFuncionarios({ status: 'ativo', per_page: 200 })
      .then(res => setFuncionarios(res.data?.data?.data ?? res.data?.data ?? []))
      .catch(() => {});
  }, []);

  const setField = (f) => (e) => setForm(prev => ({ ...prev, [f]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSalvando(true);
    setErro(null);
    try {
      await dpService.criarRescisao(form);
      setMostrarForm(false);
      await carregar();
    } catch (err) {
      setErro(err?.response?.data?.message ?? 'Erro ao salvar');
    } finally {
      setSalvando(false);
    }
  };

  const verDetalhe = async (id) => {
    try {
      const res = await dpService.buscarRescisao(id);
      setDetalhe(res.data?.data ?? null);
    } catch {
      setErro('Erro ao carregar detalhes');
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <UserMinus size={20} className="text-teal-600" />
          <h2 className="text-base font-bold text-[color:var(--text)]">Rescisões</h2>
        </div>
        {podeEditar && (
          <button onClick={() => setMostrarForm(v => !v)}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-xl bg-teal-600 text-white hover:bg-teal-700 transition-colors">
            <Plus size={14} /> Nova Rescisão
          </button>
        )}
      </div>

      {erro && <div className="text-sm text-red-600 bg-red-50 dark:bg-red-900/20 p-3 rounded-xl">{erro}</div>}

      {mostrarForm && (
        <form onSubmit={handleSubmit} className="border border-[color:var(--border)] rounded-2xl p-5 space-y-4 bg-[color:var(--surface-muted)]">
          <h3 className="text-sm font-semibold text-[color:var(--text)]">Nova Rescisão</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1">Funcionário *</label><select required value={form.funcionario_id} onChange={setField('funcionario_id')} className={inputClass}><option value="">Selecione</option>{funcionarios.map(f => <option key={f.id} value={f.id}>{f.pessoa?.nome}</option>)}</select></div>
            <div><label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1">Tipo *</label><select required value={form.tipo_rescisao} onChange={setField('tipo_rescisao')} className={inputClass}><option value="sem_justa_causa">Sem Justa Causa</option><option value="com_justa_causa">Com Justa Causa</option><option value="pedido_demissao">Pedido de Demissão</option><option value="acordo_mutuo">Acordo Mútuo</option><option value="termino_contrato">Término de Contrato</option></select></div>
            <div><label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1">Data do Aviso</label><input type="date" value={form.data_aviso} onChange={setField('data_aviso')} className={inputClass} /></div>
            <div><label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1">Data Desligamento *</label><input type="date" required value={form.data_desligamento} onChange={setField('data_desligamento')} className={inputClass} /></div>
            <div className="sm:col-span-2"><label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1">Motivo</label><input value={form.motivo} onChange={setField('motivo')} className={inputClass} /></div>
          </div>
          <div className="flex gap-3 justify-end">
            <button type="button" onClick={() => setMostrarForm(false)} className="px-4 py-2 text-sm rounded-xl border border-[color:var(--border)] text-[color:var(--text-muted)]">Cancelar</button>
            <button type="submit" disabled={salvando} className="px-4 py-2 text-sm font-semibold rounded-xl bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-60">{salvando ? 'Salvando...' : 'Calcular e Salvar'}</button>
          </div>
        </form>
      )}

      {detalhe && (
        <div className="border border-[color:var(--border)] rounded-2xl p-5 space-y-3 bg-[color:var(--surface-muted)]">
          <div className="flex justify-between items-center">
            <h3 className="text-sm font-semibold text-[color:var(--text)]">Detalhes da Rescisão</h3>
            <button onClick={() => setDetalhe(null)} className="text-[color:var(--text-muted)] hover:text-[color:var(--text)]">✕</button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            <div><span className="text-[color:var(--text-muted)]">Funcionário:</span> {detalhe.funcionario?.pessoa?.nome}</div>
            <div><span className="text-[color:var(--text-muted)]">Tipo:</span> {(detalhe.tipo_rescisao || '').replace(/_/g, ' ')}</div>
            <div><span className="text-[color:var(--text-muted)]">Desligamento:</span> {formatarData(detalhe.data_desligamento)}</div>
            {detalhe.saldo_salario && <div><span className="text-[color:var(--text-muted)]">Saldo Salário:</span> {formatarMoeda(detalhe.saldo_salario)}</div>}
            {detalhe.aviso_previo && <div><span className="text-[color:var(--text-muted)]">Aviso Prévio:</span> {formatarMoeda(detalhe.aviso_previo)}</div>}
            {detalhe.ferias_proporcionais && <div><span className="text-[color:var(--text-muted)]">Férias Proporcionais:</span> {formatarMoeda(detalhe.ferias_proporcionais)}</div>}
            {detalhe.decimo_terceiro_proporcional && <div><span className="text-[color:var(--text-muted)]">13º Proporcional:</span> {formatarMoeda(detalhe.decimo_terceiro_proporcional)}</div>}
            {detalhe.multa_fgts && <div><span className="text-[color:var(--text-muted)]">Multa FGTS:</span> {formatarMoeda(detalhe.multa_fgts)}</div>}
            {detalhe.total_liquido && <div className="font-semibold"><span className="text-[color:var(--text-muted)]">Total Líquido:</span> <span className="text-teal-600">{formatarMoeda(detalhe.total_liquido)}</span></div>}
          </div>
        </div>
      )}

      {carregando ? (
        <div className="flex justify-center py-12"><div className="animate-spin w-6 h-6 border-2 border-teal-600 border-t-transparent rounded-full" /></div>
      ) : rescisoes.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <UserMinus size={40} className="text-[color:var(--text-muted)] opacity-30 mb-3" />
          <p className="text-sm text-[color:var(--text-muted)]">Nenhuma rescisão encontrada.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[color:var(--border)]">
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--surface-muted)]">
              <tr>
                {['Funcionário', 'Tipo', 'Desligamento', 'Total Líquido', 'Ações'].map(col => (
                  <th key={col} className="text-left px-4 py-3 text-xs font-semibold text-[color:var(--text-muted)] whitespace-nowrap">{col}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[color:var(--border)]">
              {rescisoes.map(r => (
                <tr key={r.id} className="hover:bg-[color:var(--surface-muted)] transition-colors">
                  <td className="px-4 py-3 font-medium text-[color:var(--text)]">{r.funcionario?.pessoa?.nome ?? '—'}</td>
                  <td className="px-4 py-3 text-[color:var(--text-muted)] capitalize">{(r.tipo_rescisao || '').replace(/_/g, ' ')}</td>
                  <td className="px-4 py-3 text-[color:var(--text-muted)]">{formatarData(r.data_desligamento)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-teal-600">{formatarMoeda(r.total_liquido)}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => verDetalhe(r.id)} title="Ver detalhes"
                      className="p-1.5 rounded-lg text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors">
                      <Eye size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
